import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const paymentId = searchParams.get('paymentId');
  const status = searchParams.get('status');

  useEffect(() => {
    if (status === 'cancelled') {
      setResult({ success: false, message: 'Payment was cancelled' });
      setVerifying(false);
      return;
    }
    if (status === 'failed') {
      setResult({ success: false, message: 'Payment failed. Please try again.' });
      setVerifying(false);
      return;
    }
    if (paymentId) {
      verifyWithRetries();
    } else {
      setResult({ success: false, message: 'No payment information found' });
      setVerifying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, status]);

  const verifyWithRetries = async (attempt = 1, maxAttempts = 5) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/payments/verify/${paymentId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (data.token) {
            localStorage.setItem('token', data.token);
            if (setUser && data.user) setUser(data.user);
          }
          setResult(data);
          setVerifying(false);
        } else if (attempt < maxAttempts) {
          setTimeout(() => verifyWithRetries(attempt + 1, maxAttempts), 2000);
        } else {
          setResult({ success: false, message: 'Payment is still processing. Please check your dashboard in a few minutes, or contact support if the issue persists.' });
          setVerifying(false);
        }
      } else {
        if (attempt < maxAttempts) {
          setTimeout(() => verifyWithRetries(attempt + 1, maxAttempts), 2000);
        } else {
          setResult({ success: false, message: 'Could not verify payment. Please contact support.' });
          setVerifying(false);
        }
      }
    } catch (error) {
      if (attempt < maxAttempts) {
        setTimeout(() => verifyWithRetries(attempt + 1, maxAttempts), 2000);
      } else {
        setResult({ success: false, message: 'Verification error. Please contact support.' });
        setVerifying(false);
      }
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password saved! Welcome aboard.');
        navigate('/dashboard');
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Could not save password');
        setSaving(false);
      }
    } catch {
      toast.error('Connection error. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="max-w-md w-full p-10 bg-white/5 border border-white/10 rounded-2xl text-center" data-testid="payment-result-card">
          {verifying ? (
            <div>
              <Loader2 className="w-16 h-16 text-[#C6A97C] animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                Verifying Payment...
              </h2>
              <p className="text-gray-400">Please wait while we confirm your payment</p>
            </div>
          ) : result?.success && result?.needs_password ? (
            <div data-testid="set-password-panel">
              <CheckCircle className="w-16 h-16 text-[#C6A97C] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                Payment Successful!
              </h2>
              <p className="text-gray-400 mb-6">
                Your access is ready. Create a password so you can log back in anytime.
              </p>
              <form onSubmit={handleSetPassword} className="space-y-4 text-left">
                <div>
                  <Label htmlFor="new-password" className="text-gray-300">Create Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    data-testid="set-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1 bg-white/10 border-white/20 text-white"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-gray-300">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    data-testid="confirm-password-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-1 bg-white/10 border-white/20 text-white"
                    placeholder="Repeat password"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="set-password-submit-btn"
                  disabled={saving}
                  className="w-full bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full py-6 text-lg font-bold"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><KeyRound className="w-5 h-5 mr-2" /> Save & Access My Program</>}
                </Button>
              </form>
              <button
                data-testid="skip-password-btn"
                onClick={() => navigate('/dashboard')}
                className="mt-4 text-sm text-gray-400 hover:text-white underline"
              >
                Skip for now — take me to my program
              </button>
            </div>
          ) : result?.success ? (
            <div>
              <CheckCircle className="w-16 h-16 text-[#C6A97C] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                Payment Successful!
              </h2>
              <p className="text-gray-400 mb-8">{result.message || 'Your access has been activated. Enjoy your journey!'}</p>
              <div className="space-y-3">
                <Button
                  data-testid="go-dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full py-6 text-lg font-bold"
                >
                  Go to My Program
                </Button>
                <Button
                  onClick={() => navigate('/courses')}
                  variant="outline"
                  className="w-full rounded-full border-white/20 text-white hover:bg-white/5"
                >
                  Browse Courses
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                {status === 'cancelled' ? 'Payment Cancelled' : 'Payment Issue'}
              </h2>
              <p className="text-gray-400 mb-8">{result?.message || 'Something went wrong with your payment'}</p>
              <div className="space-y-3">
                <Button
                  data-testid="try-again-btn"
                  onClick={() => navigate(-1)}
                  className="w-full bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full py-6 text-lg font-bold"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full rounded-full border-white/20 text-white hover:bg-white/5"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
